import { useRoutes } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { setNavigator } from './common/navigation';
import { eDiscoveryUrl } from './common/routes';
import routes from './router';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import ThemeProvider from './theme/ThemeProvider';
import { CssBaseline } from '@mui/material';
import { useEffect, useState } from 'react';
import { Keywordcontext } from './context/keywordcontext';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { setGlobalState, useGlobalState } from './state/globalstate';
import IdleTimer from './session/idle';
import { version } from 'react';
import Axios, { API } from './common/api';
import { identifyFromStorage } from './common/analytics';
import * as crypto from 'crypto';
import React from 'react';
// Custom socket chatbot is active — Voiceflow widget (public/index.html) is disabled.
import ArkChatbot from './components/ArkChatbot';

// import logo from './logo.svg';

declare global {
  interface Window {
    analytics: any;
  }
}

const App = () => {
  // // //console.log(version);
  const [keyword, setKeyword] = useState<string>('');
  const value = { keyword, setKeyword };
  const content = useRoutes(routes);
  const location = useLocation();
  const navigate = useNavigate();

  // Expose navigate to non-React code (e.g. Axios interceptors) so they can
  // redirect (e.g. to /login on logout) without a full page reload.
  useEffect(() => {
    setNavigator(navigate);
  }, [navigate]);

  // Re-identify a signed-in user to PostHog whenever the app re-opens, so
  // returning sessions are attributed without waiting for a fresh login.
  useEffect(() => {
    identifyFromStorage();
  }, []);

  // Global connectivity notice: the moment the device goes offline, show a
  // persistent toast so the user is never left wondering why an action (upload,
  // saving tactic details, file processing, etc.) appears frozen.
  useEffect(() => {
    const OFFLINE_TOAST_ID = 'network-offline';

    const handleOffline = () => {
      toast.error(
        'You are offline. Please check your internet connection and try again.',
        {
          toastId: OFFLINE_TOAST_ID,
          autoClose: false,
          closeOnClick: false,
          draggable: false
        }
      );
    };

    const handleOnline = () => {
      toast.dismiss(OFFLINE_TOAST_ID);
      toast.success('Back online.', { autoClose: 2000 });
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    // If we load while already offline, surface it immediately.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      handleOffline();
    }

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);
  var CryptoTS = require('crypto-ts');

  const [modalmessage] = useGlobalState('modalmessage');

  if (modalmessage) {
    modalmessage;
    setGlobalState('modalmessage', null);
  }

  const [loggedin] = useGlobalState('loggedin');

  //Remove console messages
  if (process.env.NODE_ENV === 'production') {
    console.log = () => {};
    console.error = () => {};
    console.debug = () => {};
    console.warn = () => {};
    console.info = () => {};
    console.group = () => {};
    console.groupCollapsed = () => {};
    console.trace = () => {};
  } else if (process.env.NODE_ENV === 'development') {
    console.info = () => {};
    console.group = () => {};
    console.warn = () => {};
    console.groupCollapsed = () => {};
    console.trace = () => {};
  }

  const isPublicRoute = (pathname: string) => {
    if (!pathname) return true;
    if (pathname === '/' || pathname === '/login') return true;
    if (pathname.startsWith('/register')) return true;
    if (pathname.startsWith('/Awsauth')) return true;
    if (pathname.startsWith('/AccountCreated')) return true;
    if (pathname.startsWith('/forget')) return true;
    if (pathname.startsWith('/status')) return true;
    if (pathname.startsWith('/msearch')) return true;
    if (pathname.startsWith('/mview')) return true;
    return false;
  };

  // Cross-tab logout: when any OTHER tab clears the auth token — manual logout,
  // idle/session timeout, or a 401 — this tab must drop to the login screen too.
  // A `storage` event fires only in the other tabs (never the one that made the
  // change), so this never double-handles the originating tab. Our logout paths
  // wipe storage with localStorage.clear(), which dispatches an event whose
  // key === null; a direct removeItem('token') would surface key === 'token'.
  useEffect(() => {
    const handleAuthStorage = (e: StorageEvent) => {
      const tokenCleared =
        e.key === null || (e.key === 'token' && !e.newValue);
      if (tokenCleared) {
        // Guard against a clear-then-reset sequence leaving us still authed.
        if (localStorage.getItem('token')) return;
        setGlobalState('loggedin', false);
        if (!isPublicRoute(window.location.pathname)) {
          navigate('/login');
        }
        return;
      }

      // Session established in another tab: a token now exists. If this tab is
      // still sitting on a public/auth page, jump it to the dashboard landing
      // route (matching the login success handler). This stops a stale /login
      // tab from lingering, going idle, and later clearing the shared token.
      const tokenSet =
        e.key === null || (e.key === 'token' && !!e.newValue);
      if (tokenSet && localStorage.getItem('token')) {
        if (isPublicRoute(window.location.pathname)) {
          setGlobalState('loggedin', true);
          navigate(eDiscoveryUrl.CaseS3 + '/1');
        }
      }
    };
    window.addEventListener('storage', handleAuthStorage);
    return () => window.removeEventListener('storage', handleAuthStorage);
  }, [navigate]);

  // Bootstrap data for current route
  useEffect(() => {
    let isMounted = true;

    // Get language parameters (needed on login/public screens too)
    const language = async () => {
      let selectedLanguage = localStorage.getItem('language');
      let jsonFile = 1;
      if (!selectedLanguage || selectedLanguage == 'null') {
        jsonFile = 2;
        selectedLanguage = 'US - Commercial';
        localStorage.setItem('languageType', '2');
      }

      if (selectedLanguage == '中国 - 商业') {
        jsonFile = 1;
        localStorage.setItem('languageType', '1');
      } else if (selectedLanguage == 'US - Commercial') {
        jsonFile = 2;
        localStorage.setItem('languageType', '2');
      }

      const params = { commercialId: jsonFile };
      //Language listing
      try {
        await Axios.get(API.getCommercials, {
          skipRetry: true,
          timeout: 8000,
          params
        } as any).then(async (res) => {
          if (!isMounted) return;
          const languageResponse = res.data;
          // console.log(languageResponse);
          setGlobalState('languageArray', languageResponse);
          localStorage.setItem('language', selectedLanguage);
          return Promise.resolve();
        });
      } catch (error) {
        // const ERROR_MSG = 'Unable to proceed with your request';
        // setGlobalState('modalmessage', toast.error(ERROR_MSG));
        return Promise.reject();
      }
    };
    language();

    // Keep login/public route fast: skip heavy bootstrap calls there.
    const token = localStorage.getItem('token');
    if (!token || isPublicRoute(location.pathname)) {
      return () => {
        isMounted = false;
      };
    }

    const fetchAccessEmails = Axios.get(API.smartSerachAccessEmails, {
      skipRetry: true,
      timeout: 8000
    } as any)
      .then((res) => {
        if (!isMounted) return;
        const emailsArray = res.data;
        setGlobalState('smartSerachAccessEmails', emailsArray);
      })
      .catch(() => {
        // Non-blocking startup request.
      });

    const fetchS3Path = Axios.get(API.S3UploadPath, {
      skipRetry: true,
      timeout: 8000
    } as any)
      .then((res) => {
        if (!isMounted) return;
        setGlobalState('BucketType', res.data.s3BucketName.toString());
        setGlobalState('BucketPath', res.data.BucketPath.toString());
        var bytes = CryptoTS.AES.decrypt(
          res.data.accessKeyId,
          process.env.REACT_APP_DECRYPTION_KEY
        );
        var accessKeyId = bytes.toString(CryptoTS.enc.Utf8);
        setGlobalState('accessKeyId', accessKeyId);
        var bytes2 = CryptoTS.AES.decrypt(
          res.data.secretAccessKey,
          process.env.REACT_APP_DECRYPTION_KEY
        );
        var secretAccessKey = bytes2.toString(CryptoTS.enc.Utf8);
        setGlobalState('secretAccessKey', secretAccessKey);
        setGlobalState('BucketRegion', res.data.BucketRegion);
        setGlobalState('ErrorFoundGlobal', true);
      })
      .catch(() => {
        if (!isMounted) return;
        setGlobalState('ErrorFoundGlobal', false);
      });

    Promise.allSettled([fetchAccessEmails, fetchS3Path]);

    return () => {
      isMounted = false;
    };
  }, [location.pathname]);

  // Replaces the Voiceflow widget and, like it, shows on every page. Keyed by
  // user so logging in/out remounts it: the previous user's conversation must
  // never stay on screen for the next person at the login page.
  const chatbotUserKey = localStorage.getItem('userId') ?? 'anon';

  return (
    <ThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <CssBaseline />

        <IdleTimer />
        <Keywordcontext.Provider value={value}>
          {content}
        </Keywordcontext.Provider>
        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        <ArkChatbot key={chatbotUserKey} />
      </LocalizationProvider>
    </ThemeProvider>
  );
};
export default App;
